export const getFilterValue = (array = [], _key) => {
	const values = [...array].map(v => v[_key]);
	const unique = new Set(values);
	return [...unique]
		.map(v => ({text: v, value: v}))
		.sort((a, b) => String(a.text ?? "").localeCompare(String(b.text ?? "")));
};

export const getBoolFilterValue = (array = [], _key) => {
	const values = [...array].map(v => v[_key]);
	const unique = new Set(values);
	return [...unique].map(v => ({text: v ? "Yes" : "No", value: v})).sort((a, b) => a.text.localeCompare(b.text));
};

export function sortByKeys(values, keys) {
	return [...values].sort((a, b) => keys.indexOf(a.key) - keys.indexOf(b.key));
}

const BOM_CATEGORY_ORDER = ["M", "S"];

export function compareBomCategory(a, b) {
	const rank = value => {
		const index = BOM_CATEGORY_ORDER.indexOf(value);
		return index === -1 ? BOM_CATEGORY_ORDER.length : index;
	};

	return rank(a) - rank(b);
}

export function buildMaintenanceParams(maintenance) {
	return maintenance ? {maintenance} : {};
}

export function buildQuery(params = {}) {
	const searchParams = new URLSearchParams(params);
	return searchParams.toString();
}

export function normalizeWhitespace(str) {
	return str ? str.trim().replace(/\s+/g, " ") : "";
}

export function getSearchAttempt({query, showAll, equipment, operator, top, lastApplied, resend = false}) {
	const cleanString = normalizeWhitespace(query);
	const operatorsChanged = lastApplied && JSON.stringify(lastApplied.operator) !== JSON.stringify(operator);

	const isQueryChanged =
		!lastApplied ||
		lastApplied.query !== cleanString ||
		lastApplied.showAll !== showAll ||
		lastApplied.equipment !== equipment ||
		operatorsChanged ||
		lastApplied.top !== top;

	return {
		cleanString,
		shouldSearch: cleanString.length > 1 && (isQueryChanged || resend),
	};
}

export function getSummaryValue(columns, item) {
	if (!item || !columns) return [];
	return columns.map(key => ({
		key,
		value: item[key] || "",
	}));
}

export function getDateFromString(dateStr) {
	if (!dateStr) return new Date();

	if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
		const [year, month, day] = dateStr.split("-");
		return new Date(`${year}-${month}-${day}`);
	}

	const [day, month, year] = dateStr.split(/[.-]/);
	return new Date(`${year}-${month}-${day}`);
}

function getFirstTransactionDate(transactions) {
	if (!transactions?.length) return null;

	let firstDate = null;
	for (const record of transactions) {
		const date = getDateFromString(record?.date);
		if (isNaN(date.getTime())) continue;
		if (!firstDate || date < firstDate) {
			firstDate = date;
		}
	}
	return firstDate;
}

export function resolveUsageStartDate(createdDateStr, transactions) {
	const creationDate = getDateFromString(createdDateStr);
	const firstTransactionDate = getFirstTransactionDate(transactions);

	if (!firstTransactionDate) {
		return creationDate;
	}

	return firstTransactionDate < creationDate ? firstTransactionDate : creationDate;
}

function formatYearMonth(date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function buildFlatGraph(createdDate) {
	const start = getDateFromString(createdDate);
	const endMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
	const row = date => ({date: formatYearMonth(date), stock_level: 0, gi: 0, gr: 0});
	const rows = [];

	for (
		let current = new Date(start.getFullYear(), start.getMonth(), 1);
		current <= endMonth;
		current = new Date(current.getFullYear(), current.getMonth() + 1, 1)
	) {
		rows.push(row(current));
	}

	if (rows.length < 2) {
		rows.unshift(row(new Date(start.getFullYear(), start.getMonth() - 1, 1)));
	}

	return rows;
}

export function formatUsageStats(rawResponseData, months, indices, startDate) {
	const currentYear = new Date().getFullYear();
	const creationDate = startDate instanceof Date ? startDate : getDateFromString(startDate);
	const creationYear = creationDate.getFullYear();

	const stats = [];
	let totalCount = 0;

	for (let year = creationYear; year <= currentYear; year++) {
		const yearStats = {year, total: 0};

		months.forEach(month => {
			const value = rawResponseData?.[year]?.[indices[month]] ?? 0;
			yearStats[month] = value;
			yearStats.total += value;
		});

		totalCount += yearStats.total;
		stats.push(yearStats);
	}

	return {
		stats: stats.sort((a, b) => b.year - a.year),
		totalCount,
		creationDate,
	};
}

export function createOperators(full, selected) {
	const result = {};
	for (const hub in full) {
		for (const operator in full[hub].operators) {
			if (selected.indexOf(full[hub].operators[operator]) > -1) {
				if (!(full[hub].name in result)) {
					result[full[hub].name] = [];
				}
				result[full[hub].name].push(full[hub].operators[operator]);
			}
		}
	}
	return {operators: result};
}

export function getKeys(item) {
	return {
		operator: item.operator,
		material: item.material,
		score: item.score.toFixed(2),
	};
}

function createItems(hub) {
	return hub.operators;
}

export function getDefaultOperators(data) {
	const allOperators = [];
	for (const hub of data) {
		if (hub.operators) {
			for (const operator of hub.operators) {
				if (!allOperators.includes(operator)) {
					allOperators.push(operator);
				}
			}
		}
	}
	return allOperators;
}

// TODO: Verify casing logic. Currently: replication Lodash startCase behavior:
export function beautifyKey(key) {
	if (!key) return "";

	return key
		.split("_")
		.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join(" ");
}
